import React, { useState, useEffect } from 'react';
import { Box, Grid, Accordion, AccordionSummary, AccordionDetails, Typography, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CustomCheckbox from './CustomCheckbox';
import ModalColumnTitle from './Modals/components/ModalColumnTitle';
import { PRIVILEGES_TYPES } from '../constants/privileges';
import CustomInput from './Inputs';
import { getMunicipalDistrictsRequest } from '../api/municipalDistrictsApi';

export default function PrivilegeCheckboxes({
  privilegeData, onChange, setRole, onManageTsooRights, showOnlyTsoo = false,
}) {
  const [filter, setFilter] = useState('');
  const [municipalDistricts, setMunicipalDistricts] = useState([]);

  useEffect(() => {
    const loadMunicipalDistricts = async () => {
      try {
        const districts = await getMunicipalDistrictsRequest();
        setMunicipalDistricts(districts || []);
      } catch (error) {
        console.error('Failed to load municipal districts:', error);
      }
    };
    loadMunicipalDistricts();
  }, []);

  const changeFilter = (e, value) => setFilter(value);

  const onChangeGroup = (groupPrivileges, value) => {
    const groupValue = {};
    groupPrivileges.forEach((privilege) => {
      groupValue[privilege.code] = { ...privilege, value };
    });

    setRole((prevState) => ({
      ...prevState,
      privilege: {
        ...prevState.privilege,
        ...groupValue,
      },
    }));
  };

  const onChangeMunicipalDistrict = (groupCode, districtId, checked) => {
    // Получаем все привилегии группы
    const groupPrivileges = Object.values(privilegeData)
      .filter((p) => p?.code?.startsWith(groupCode));

    setRole((prevState) => {
      const updatedPrivilege = { ...prevState.privilege };

      groupPrivileges.forEach((privilege) => {
        const currentIds = updatedPrivilege[privilege.code]?.municipal_district_ids || [];
        let newIds;

        if (checked) {
          newIds = [...new Set([...currentIds, districtId])];
        } else {
          newIds = currentIds.filter((id) => id !== districtId);
        }

        updatedPrivilege[privilege.code] = {
          ...updatedPrivilege[privilege.code],
          municipal_district_ids: newIds,
        };
      });

      return {
        ...prevState,
        privilege: updatedPrivilege,
      };
    });
  };

  const onChangeAllMunicipalDistricts = (groupCode, checked) => {
    const groupPrivileges = Object.values(privilegeData)
      .filter((p) => p?.code?.startsWith(groupCode));

    const allDistrictIds = checked ? municipalDistricts.map((d) => Number(d.id)) : [];

    setRole((prevState) => {
      const updatedPrivilege = { ...prevState.privilege };

      groupPrivileges.forEach((privilege) => {
        updatedPrivilege[privilege.code] = {
          ...updatedPrivilege[privilege.code],
          municipal_district_ids: allDistrictIds,
        };
      });

      return {
        ...prevState,
        privilege: updatedPrivilege,
      };
    });
  };

  const getGroupMunicipalDistrictIds = (groupCode) => {
    const groupPrivileges = Object.values(privilegeData)
      .filter((p) => p?.code?.startsWith(groupCode));

    // Собираем все уникальные district_ids из всех привилегий группы
    const allDistrictIds = new Set();
    groupPrivileges.forEach((p) => {
      (p?.municipal_district_ids || []).forEach((id) => allDistrictIds.add(id));
    });

    const result = Array.from(allDistrictIds);
    if (groupCode === 'tsoo_active_objects') {
      console.log('getGroupMunicipalDistrictIds for tsoo_active_objects:', result);
      console.log('groupPrivileges municipal_district_ids:', groupPrivileges.map(p => ({ code: p.code, ids: p.municipal_district_ids })));
    }
    return result;
  };

  // Обработчик изменения чекбокса с логикой связывания create и update для TSOO
  const handlePrivilegeChange = (e, value, privilege) => {
    const { code } = privilege;

    // Проверяем, является ли это привилегией TSOO
    if (code?.startsWith('tsoo_')) {
      // Определяем базовый код группы (без _create/_update/_read/_destroy)
      const baseCode = code.replace(/_(create|update|read|destroy)$/, '');

      // Если это create или update, связываем их
      if (code.endsWith('_create') || code.endsWith('_update')) {
        const createCode = `${baseCode}_create`;
        const updateCode = `${baseCode}_update`;

        const createPrivilege = privilegeData[createCode];
        const updatePrivilege = privilegeData[updateCode];

        if (createPrivilege && updatePrivilege) {
          setRole((prevState) => ({
            ...prevState,
            privilege: {
              ...prevState.privilege,
              [createCode]: { ...createPrivilege, value },
              [updateCode]: { ...updatePrivilege, value },
            },
          }));
          return;
        }
      }
    }

    // Стандартное поведение для остальных привилегий
    onChange(e, value, privilege);
  };

  // Проверяем, есть ли у группы привилегий флаг has_municipal_districts
  const groupHasMunicipalDistricts = (idType) => {
    const privileges = Object.values(privilegeData)
      .filter((privilege) => privilege?.code?.includes?.(idType));

    return privileges.some(p =>
      p?.privilege_group_attributes?.has_municipal_districts === true
    );
  };

  return (
    <Box>
      <ModalColumnTitle>Привилегии</ModalColumnTitle>
      <CustomInput
        sx={{ width: '320px', mb: 2 }}
        onChange={changeFilter}
        label="Поиск по группе привилегий"
        value={filter}
      />
      <Grid container gap={2} flexWrap="wrap">
        {PRIVILEGES_TYPES
          .filter(({ id, label }) => {
            // Фильтр по поиску
            if (!label.toLowerCase().includes(filter.toLowerCase())) return false;

            // Фильтр TSOO/не-TSOO
            const isTsoo = id.startsWith('tsoo_');
            if (showOnlyTsoo) return isTsoo;
            return !isTsoo;
          })
          .map(({ id: idType, label }) => {
            const filteredPrivileges = Object.values(privilegeData)
              .filter((privilege) => privilege?.code?.includes?.(idType));

            const hasMunicipalDistricts = groupHasMunicipalDistricts(idType);

            const isAdminTerScheme = idType === 'admin_ter_scheme';

            return (
              <Grid
                sx={{ background: 'rgb(0,0,0,0.1)', p: 3, borderRadius: '20px', position: 'relative' }}
                item
                xs={2.9}
                key={idType}
                display="flex"
                flexDirection="column"
              >
                {!isAdminTerScheme && (
                  <CustomCheckbox
                    sx={{ '& .MuiFormControlLabel-label': { fontWeight: 'bold', fontSize: 18 }, mb: 1 }}
                    key={idType}
                    id={idType}
                    name={idType}
                    label={label}
                    value={filteredPrivileges.every(({ code }) => privilegeData[code].value)}
                    onChange={(e, value) => onChangeGroup(filteredPrivileges, value)}
                  />
                )}
                {isAdminTerScheme && (
                  <Typography sx={{ fontWeight: 'bold', fontSize: 18, mb: 1, ml: 1.5 }}>
                    {label}
                  </Typography>
                )}
                {filteredPrivileges.map((filteredPrivilege) => (
                  <CustomCheckbox
                    key={filteredPrivilege.id}
                    id={filteredPrivilege.id}
                    name={filteredPrivilege.code}
                    label={filteredPrivilege.name}
                    value={privilegeData[filteredPrivilege.code].value}
                    onChange={(e, value) => handlePrivilegeChange(e, value, filteredPrivilege)}
                  />
                ))}
                {hasMunicipalDistricts && (
                  <Accordion sx={{ mt: 2, boxShadow: 'none', '&:before': { display: 'none' }, background: 'transparent' }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                      sx={{
                        px: 0,
                        minHeight: 'auto',
                        flexDirection: 'row-reverse',
                        '& .MuiAccordionSummary-content': { my: 1, ml: 1 },
                        '& .MuiAccordionSummary-expandIconWrapper': { mr: 0 },
                      }}
                    >
                      <Typography sx={{ color: 'primary.main', fontWeight: 500 }}>
                        Муниципальные районы
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pt: 0 }}>
                      <CustomCheckbox
                        id={`${idType}_all_districts`}
                        name={`${idType}_all_districts`}
                        label="Все муниципальные районы"
                        value={getGroupMunicipalDistrictIds(idType).length === municipalDistricts.length}
                        onChange={(e, checked) => onChangeAllMunicipalDistricts(idType, checked)}
                      />
                      {municipalDistricts.map((district) => (
                        <CustomCheckbox
                          key={district.id}
                          id={`${idType}_district_${district.id}`}
                          name={`${idType}_district_${district.id}`}
                          label={district.name}
                          value={getGroupMunicipalDistrictIds(idType).includes(Number(district.id))}
                          onChange={(e, checked) => onChangeMunicipalDistrict(idType, Number(district.id), checked)}
                        />
                      ))}
                    </AccordionDetails>
                  </Accordion>
                )}

                {isAdminTerScheme && onManageTsooRights && (
                  <Box sx={{ position: 'absolute', bottom: 12, right: 12 }}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={onManageTsooRights}
                      sx={{ fontSize: '12px', textTransform: 'none' }}
                    >
                      Управление правами
                    </Button>
                  </Box>
                )}
              </Grid>
            );
          })}
      </Grid>
    </Box>
  );
}
